import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  FULL_WEIGHT_BP,
  RISK_RATINGS,
  formatScore,
  formatWeight,
  validateRiskModel,
  type RiskModelDefinition,
  type RiskRatingValue,
} from '@norvix/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { riskScoringApi } from '../../api/risk-scoring.api.js';
import { settingsHistoryKey, type SettingsHistoryEntry } from '../../api/settings-history.api.js';
import { RISK_SCORING_RESTORE, fourEyesRestoreApi } from '../../api/settings-restore.api.js';
import { APP_LOCALE } from '../../utils/locale.js';

import { SettingsHistoryButton, useSettingsLastChanged } from './SettingsHistory.js';
import { FourEyesRestore } from './SettingsRestore.js';
import { SettingsSectionHeader } from './SettingsSectionHeader.js';
import { useSectionRestore } from './useSettingsAccess.js';

const RISK_RESTORE = fourEyesRestoreApi(RISK_SCORING_RESTORE);

/**
 * The weighted risk-scoring model (DEV-90), as the compliance team defines it.
 *
 * Nothing is built in: categories, criteria, weights, the score each risk level
 * carries and the thresholds that classify a total are all entered here, so the
 * model can be made to match the provider's and both sides agree on a client.
 * Each save is a new version; assessments keep the version they were made on.
 * Every version says why it was made (DEV-137): the API refuses one without a
 * reason, and the History compares each version with the one before.
 */

interface DraftCriterion { id: string; name: string; weight: string }
interface DraftCategory { id: string; name: string; criteria: DraftCriterion[] }
interface DraftLevel { level: string; label: string; score: string }
type DraftThresholds = Record<RiskRatingValue, string>;

const newId = (): string => (typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `id-${String(Date.now())}-${String(Math.random()).slice(2, 8)}`);

/** "2.5" → 250 basis points; null when it is not a percentage to two decimals. */
function toBp(percent: string): number | null {
  const value = percent.trim().replace(',', '.');
  if (!/^[0-9]{1,3}(\.[0-9]{1,2})?$/.test(value)) return null;
  const [whole = '0', fraction = ''] = value.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

/** "3.5" → 350 hundredths; null when blank or malformed. */
function toHundredths(score: string): number | null {
  const value = score.trim().replace(',', '.');
  if (!/^[0-9]{1,3}(\.[0-9]{1,2})?$/.test(value)) return null;
  const [whole = '0', fraction = ''] = value.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

const bpToPercent = (bp: number): string => formatWeight(bp).replace('%', '');

function fromDefinition(definition: RiskModelDefinition): { categories: DraftCategory[]; levels: DraftLevel[]; thresholds: DraftThresholds } {
  const thresholds: DraftThresholds = { LOW: '0', MEDIUM: '', HIGH: '', PROHIBITED: '' };
  for (const threshold of definition.thresholds) thresholds[threshold.rating] = formatScore(threshold.minHundredths);
  return {
    categories: definition.categories.map((category) => ({
      id: category.id, name: category.name,
      criteria: category.criteria.map((criterion) => ({ id: criterion.id, name: criterion.name, weight: bpToPercent(criterion.weightBp) })),
    })),
    levels: definition.levels.map((level) => ({ level: level.level, label: level.label, score: String(level.score) })),
    thresholds,
  };
}

/**
 * A starting point, not a model: the one category the provider's example
 * showed (DEV-90), and a five-step level scale. Nothing is saved until the
 * team completes it and the weights add up.
 */
function starter(): { categories: DraftCategory[]; levels: DraftLevel[]; thresholds: DraftThresholds } {
  return {
    categories: [{
      id: newId(), name: 'Geographic Factors', criteria: [
        { id: newId(), name: 'Country of Incorporation', weight: '5' },
        { id: newId(), name: 'Primary Regulatory Body', weight: '5' },
        { id: newId(), name: 'Country of Operation', weight: '10' },
        { id: newId(), name: 'Country of Destination of Funds', weight: '5' },
      ],
    }],
    levels: [
      { level: 'LOW', label: 'Low', score: '1' },
      { level: 'MEDIUM', label: 'Medium', score: '2' },
      { level: 'HIGH', label: 'High', score: '3' },
      { level: 'VERY_HIGH', label: 'Very high', score: '4' },
      { level: 'PROHIBITED', label: 'Prohibited', score: '5' },
    ],
    thresholds: { LOW: '0', MEDIUM: '', HIGH: '', PROHIBITED: '' },
  };
}

export function RiskScoringModelPage(): JSX.Element {
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['risk-scoring-model'], queryFn: riskScoringApi.model });
  const queryClient = useQueryClient();
  const lastChanged = useSettingsLastChanged('RISK_SCORING');
  const [categories, setCategories] = useState<DraftCategory[]>([]);
  const [levels, setLevels] = useState<DraftLevel[]>([]);
  const [thresholds, setThresholds] = useState<DraftThresholds>({ LOW: '0', MEDIUM: '', HIGH: '', PROHIBITED: '' });
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [loadedVersion, setLoadedVersion] = useState<number | null | undefined>(undefined);
  const restore = useSectionRestore({ group: 'compliance', id: 'risk-scoring' });
  const [restoreTarget, setRestoreTarget] = useState<string | null>(null);

  useEffect(() => {
    if (data === undefined) return;
    const version = data.active?.version ?? null;
    if (version === loadedVersion) return;
    const draft = data.active === null ? { categories: [], levels: [], thresholds: { LOW: '0', MEDIUM: '', HIGH: '', PROHIBITED: '' } } : fromDefinition(data.active.definition);
    setCategories(draft.categories);
    setLevels(draft.levels);
    setThresholds(draft.thresholds);
    setLoadedVersion(version);
  }, [data, loadedVersion]);

  if (isLoading) return <CircularProgress aria-label="Loading the scoring model" />;
  if (error !== null || data === undefined) {
    return <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>The scoring model could not be loaded.</Alert>;
  }

  const formatProblems: string[] = [];
  const definition: RiskModelDefinition = {
    categories: categories.map((category) => ({
      id: category.id, name: category.name,
      criteria: category.criteria.map((criterion) => {
        const bp = toBp(criterion.weight);
        if (bp === null) formatProblems.push(`"${criterion.name || 'A criterion'}": give its weight as a percentage, e.g. 5 or 2.5.`);
        return { id: criterion.id, name: criterion.name, weightBp: bp ?? 0 };
      }),
    })),
    levels: levels.map((level) => {
      if (!/^[0-9]{1,3}$/.test(level.score.trim())) formatProblems.push(`Risk level "${level.label || level.level || 'new'}" needs a whole-number score.`);
      return { level: level.level.trim().toUpperCase(), label: level.label, score: Number(level.score.trim() === '' ? NaN : level.score) };
    }),
    thresholds: RISK_RATINGS.filter((rating) => thresholds[rating].trim() !== '').map((rating) => {
      const min = toHundredths(thresholds[rating]);
      if (min === null) formatProblems.push(`The ${rating} threshold must be a score such as 3.5.`);
      return { rating, minHundredths: min ?? -1 };
    }),
  };
  const problems = [...formatProblems, ...validateRiskModel(definition)];
  const totalBp = definition.categories.reduce((sum, c) => sum + c.criteria.reduce((s, x) => s + x.weightBp, 0), 0);

  const setCategory = (index: number, change: Partial<DraftCategory>): void =>
    setCategories((prev) => prev.map((category, i) => (i === index ? { ...category, ...change } : category)));
  const setCriterion = (ci: number, xi: number, change: Partial<DraftCriterion>): void =>
    setCategories((prev) => prev.map((category, i) => (i !== ci ? category : {
      ...category, criteria: category.criteria.map((criterion, j) => (j === xi ? { ...criterion, ...change } : criterion)),
    })));

  const save = (): void => {
    setSaving(true);
    setSaveError('');
    riskScoringApi.saveModel(definition, note)
      .then(async (saved) => {
        setSavedVersion(saved.version);
        setNote('');
        void queryClient.invalidateQueries({ queryKey: settingsHistoryKey('RISK_SCORING') });
        await refetch();
      })
      .catch((err: unknown) => setSaveError(err instanceof Error ? err.message : 'The model could not be saved.'))
      .finally(() => setSaving(false));
  };

  const start = (): void => {
    const draft = starter();
    setCategories(draft.categories);
    setLevels(draft.levels);
    setThresholds(draft.thresholds);
  };

  return (
    <Stack spacing={3}>
      <SettingsSectionHeader
        title="Risk scoring model"
        lastChanged={lastChanged}
        actions={<SettingsHistoryButton subject="RISK_SCORING" title="Risk scoring" restoreLabel={restore.fourEyes ? 'Propose restore' : 'Restore this version'}
          {...(restore.note === undefined ? {} : { restoreNote: restore.note })}
          {...(restore.canRestore ? { onRestore: (entry: SettingsHistoryEntry) => setRestoreTarget(entry.restoreTarget ?? null) } : {})} />}
      >
        The weighted model officers score clients against. Each criterion's weight is its share of the
        whole, so a category weighs what its criteria add up to, and all weights add up to 100%. A total is
        the weighted mean of the level scores; the thresholds classify it. Every new version needs a reason.
        {data.active !== null ? ` In force: version ${String(data.active.version)}.` : ''}
      </SettingsSectionHeader>

      {data.active === null && categories.length === 0 ? (
        <Alert severity="info" action={<Button onClick={start}>Start from the provider's example</Button>}>
          No model is configured yet, so clients cannot be scored. Enter the provider's categories, criteria
          and weights here.
        </Alert>
      ) : null}
      {savedVersion !== null ? <Alert severity="success" role="status">Saved as version {String(savedVersion)}.</Alert> : null}
      {/* DEV-142: a restore needs a second approver. */}
      <FourEyesRestore
        api={RISK_RESTORE}
        base={RISK_SCORING_RESTORE}
        subject="RISK_SCORING"
        target={restoreTarget}
        onClearTarget={() => setRestoreTarget(null)}
        canDecide={restore.canRestore}
        onApproved={() => { setLoadedVersion(undefined); void refetch(); }}
      />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" component="h3">Categories and criteria</Typography>
          <Typography variant="body2" color={totalBp === FULL_WEIGHT_BP ? 'success.main' : 'warning.main'} role="status" aria-label="Total weight">
            Total weight {formatWeight(totalBp)}
          </Typography>
        </Stack>
        <Stack spacing={3}>
          {categories.map((category, ci) => {
            const weight = definition.categories[ci]?.criteria.reduce((s, x) => s + x.weightBp, 0) ?? 0;
            return (
              <Box key={category.id} component="fieldset" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, m: 0 }} aria-label={`Category ${category.name || String(ci + 1)}`}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <TextField size="small" label="Category" value={category.name} sx={{ flex: 1 }}
                    onChange={(event) => setCategory(ci, { name: event.target.value })} inputProps={{ maxLength: 120 }} />
                  <Typography variant="body2" sx={{ minWidth: 90 }}>{formatWeight(weight)}</Typography>
                  <Button size="small" color="error" onClick={() => setCategories((prev) => prev.filter((_, i) => i !== ci))}>Remove category</Button>
                </Stack>
                <Stack spacing={1} sx={{ mt: 1.5, pl: { sm: 2 } }}>
                  {category.criteria.map((criterion, xi) => (
                    <Stack key={criterion.id} direction="row" spacing={1} alignItems="center">
                      <TextField size="small" label="Criterion" value={criterion.name} sx={{ flex: 1 }}
                        onChange={(event) => setCriterion(ci, xi, { name: event.target.value })} inputProps={{ maxLength: 120 }} />
                      <TextField size="small" label="Weight %" value={criterion.weight} sx={{ width: 110 }}
                        onChange={(event) => setCriterion(ci, xi, { weight: event.target.value })} inputProps={{ inputMode: 'decimal' }} />
                      <IconButton aria-label={`Remove ${criterion.name || 'criterion'}`} size="small"
                        onClick={() => setCategory(ci, { criteria: category.criteria.filter((_, j) => j !== xi) })}>×</IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button size="small" onClick={() => setCategory(ci, { criteria: [...category.criteria, { id: newId(), name: '', weight: '' }] })}>
                      Add criterion
                    </Button>
                  </Box>
                </Stack>
              </Box>
            );
          })}
          <Box>
            <Button variant="outlined" size="small" onClick={() => setCategories((prev) => [...prev, { id: newId(), name: '', criteria: [] }])}>
              Add category
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" component="h3" gutterBottom>Risk levels and their scores</Typography>
        <Stack spacing={1}>
          {levels.map((level, li) => (
            <Stack key={li} direction="row" spacing={1} alignItems="center">
              <TextField size="small" label="Key" value={level.level} sx={{ width: 150 }}
                onChange={(event) => setLevels((prev) => prev.map((l, i) => (i === li ? { ...l, level: event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') } : l)))} />
              <TextField size="small" label="Label" value={level.label} sx={{ flex: 1 }}
                onChange={(event) => setLevels((prev) => prev.map((l, i) => (i === li ? { ...l, label: event.target.value } : l)))} />
              <TextField size="small" label="Score" value={level.score} sx={{ width: 100 }} inputProps={{ inputMode: 'numeric' }}
                onChange={(event) => setLevels((prev) => prev.map((l, i) => (i === li ? { ...l, score: event.target.value } : l)))} />
              <IconButton aria-label={`Remove level ${level.label}`} size="small" onClick={() => setLevels((prev) => prev.filter((_, i) => i !== li))}>×</IconButton>
            </Stack>
          ))}
          <Box><Button size="small" onClick={() => setLevels((prev) => [...prev, { level: '', label: '', score: '' }])}>Add level</Button></Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" component="h3" gutterBottom>Classification</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          A total at or above a threshold takes that classification. Leave one empty if the model does not use it.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {RISK_RATINGS.map((rating) => (
            <TextField key={rating} size="small" label={`${rating} from`} value={thresholds[rating]} disabled={rating === 'LOW'}
              onChange={(event) => setThresholds((prev) => ({ ...prev, [rating]: event.target.value }))} inputProps={{ inputMode: 'decimal' }} />
          ))}
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          {problems.length > 0 && categories.length > 0 ? (
            <Alert severity="warning">
              <Box component="ul" sx={{ m: 0, pl: 2 }}>{[...new Set(problems)].map((p) => <li key={p}>{p}</li>)}</Box>
            </Alert>
          ) : null}
          {saveError !== '' ? <Alert severity="error">{saveError}</Alert> : null}
          <TextField label="What changed, and why" value={note} onChange={(event) => setNote(event.target.value)} multiline minRows={2}
            required inputProps={{ maxLength: 1000 }} helperText="Required. Kept with the version and shown in the History." />
          <Box>
            <Button variant="contained" disabled={saving || problems.length > 0 || note.trim() === ''} onClick={save}>
              {saving ? 'Saving…' : data.active === null ? 'Save the first version' : `Save as version ${String(data.active.version + 1)}`}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {data.versions.length > 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle1" component="h3" gutterBottom>Versions</Typography>
          <Stack component="ol" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Model versions">
            {data.versions.map((version) => (
              <Box component="li" key={version.id}>
                <Typography variant="body2"><b>Version {String(version.version)}</b> · {new Date(version.createdAt).toLocaleString(APP_LOCALE)} · {version.createdByName ?? version.createdBy}</Typography>
                {version.note !== null ? <Typography variant="caption" color="text.secondary">{version.note}</Typography> : null}
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}
